import { loincCoding } from '#src/models/loinc.ts'
import {
  PHQ_DIFFICULTY_ANSWERS,
  PHQ_FREQUENCY_ANSWERS,
} from '#src/terminology/instruments/instrumentTerminology.ts'
import {
  scoreBand,
  scoredItem,
  totalScore,
} from '#src/terminology/instruments/templateParts.ts'
import type {
  InstrumentSection,
  InstrumentTemplate,
} from '#src/terminology/instruments/types.ts'

export const PHQ_COPYRIGHT =
  'Copyright © Pfizer Inc. All rights reserved. Developed by Drs. Robert L. Spitzer, Janet B.W. Williams, Kurt Kroenke and colleagues, with an educational grant from Pfizer Inc. No permission required to reproduce, translate, display or distribute.'

export const PHQ_DIFFICULTY_LABEL =
  'How difficult have these problems made it for you to do your work, take care of things at home, or get along with other people?'

const SECTIONS: InstrumentSection[] = [
  {
    id: 'items',
    items: [
      ['44250-9', 'Little interest or pleasure in doing things'],
      ['44255-8', 'Feeling down, depressed, or hopeless'],
      ['44259-0', 'Trouble falling or staying asleep, or sleeping too much'],
      ['44254-1', 'Feeling tired or having little energy'],
      ['44251-7', 'Poor appetite or overeating'],
      [
        '44258-2',
        'Feeling bad about yourself-or that you are a failure or have let yourself or your family down',
      ],
      [
        '44252-5',
        'Trouble concentrating on things, such as reading the newspaper or watching television',
      ],
      [
        '44253-3',
        'Moving or speaking so slowly that other people could have noticed. Or the opposite – being so fidgety or restless that you were moving around a lot more than usual',
      ],
      [
        '44260-8',
        'Thoughts that you would be better off dead, or of hurting yourself in some way',
      ],
    ]
      .map(([code, label]) =>
        scoredItem(loincCoding(code, label), PHQ_FREQUENCY_ANSWERS)
      )
      .concat(
        scoredItem(
          loincCoding('69722-7', PHQ_DIFFICULTY_LABEL),
          PHQ_DIFFICULTY_ANSWERS,
          { scaleType: 'Nom', required: false, scored: false }
        )
      ),
  },
]

export const PHQ9_TEMPLATE: InstrumentTemplate = {
  id: 'phq-9',
  version: '1',
  panel: loincCoding(
    '44249-1',
    'PHQ-9 quick depression assessment panel [Reported.PHQ]'
  ),
  title: 'Patient Health Questionnaire (PHQ-9)',
  copyright: PHQ_COPYRIGHT,
  reference:
    'https://www.apa.org/depression-guideline/patient-health-questionnaire.pdf',
  sections: SECTIONS,
  total: totalScore({
    templateId: 'phq-9',
    coding: loincCoding(
      '44261-6',
      'Patient health questionnaire 9 item total score'
    ),
    sections: SECTIONS,
    completeness: { requireAll: true },
    bands: [
      scoreBand({ min: 0, max: 0 }, 'phq-9-none', 'No Depression', 'green'),
      scoreBand(
        { min: 1, max: 4 },
        'phq-9-minimal',
        'Minimal Depression',
        'green'
      ),
      scoreBand({ min: 5, max: 9 }, 'phq-9-mild', 'Mild Depression', 'yellow'),
      scoreBand(
        { min: 10, max: 14 },
        'phq-9-moderate',
        'Moderate Depression',
        'yellow'
      ),
      scoreBand(
        { min: 15, max: 19 },
        'phq-9-moderately-severe',
        'Moderately Severe Depression',
        'orange'
      ),
      scoreBand({ min: 20 }, 'phq-9-severe', 'Severe Depression', 'red'),
    ],
  }),
}
